import { Satisfaction } from 'lib-shared-legacy-types';
import { Action, ActionFunctionAny, ActionMeta } from 'redux-actions';
import {
  setSubmitFailed,
  setSubmitSucceeded,
  startSubmit,
  stopSubmit,
} from 'redux-form';
import { call, put, takeLatest } from 'redux-saga/effects';

import { ChatPanelForms } from 'components/settings/components/sections/satisfaction/components/ChatCSAT/ChatPanel/chatPanelForms';
import { DixaMessengerPanelForms } from 'components/settings/components/sections/satisfaction/components/DixaMessengerCSAT/Panel/messengerPanelForms';
import { EmailPanelForms } from 'components/settings/components/sections/satisfaction/components/EmailCSAT/EmailPanel/emailPanelForms';
import { pubSub } from 'utils/pubSub';
import { PromisedValue } from 'utils/typeUtils';

import { notificationActions } from '../../notifications/actions/notification';
import actions from '../actions/satisfaction';
import * as api from '../api/satisfaction';

const {
  satisfactionInitializeRequest,
  satisfactionInitializeSucceeded,
  satisfactionInitializeFailed,

  // --------------------------------------------------

  satisfactionLoadEmailAllLevelsSettingsRequest,
  satisfactionLoadEmailAllLevelsSettingsSucceeded,
  satisfactionLoadEmailAllLevelsSettingsFailed,

  satisfactionLoadEmailGlobalSettingsRequest,
  satisfactionLoadEmailGlobalSettingsSucceeded,
  satisfactionLoadEmailGlobalSettingsFailed,

  satisfactionSaveEmailGlobalSettingsRequest,
  satisfactionSaveEmailGlobalSettingsSucceeded,
  satisfactionSaveEmailGlobalSettingsFailed,

  satisfactionLoadEmailContactLevelSettingsRequest,
  satisfactionLoadEmailContactLevelSettingsSucceeded,
  satisfactionLoadEmailContactLevelSettingsFailed,

  satisfactionSaveEmailContactLevelSettingsRequest,
  satisfactionSaveEmailContactLevelSettingsSucceeded,
  satisfactionSaveEmailContactLevelSettingsFailed,

  satisfactionLoadEmailLanguageLevelSettingsRequest,
  satisfactionLoadEmailLanguageLevelSettingsSucceeded,
  satisfactionLoadEmailLanguageLevelSettingsFailed,

  satisfactionSaveEmailLanguageLevelSettingsRequest,
  satisfactionSaveEmailLanguageLevelSettingsSucceeded,
  satisfactionSaveEmailLanguageLevelSettingsFailed,

  // --------------------------------------------------

  satisfactionLoadChatAllLevelsSettingsRequest,
  satisfactionLoadChatAllLevelsSettingsSucceeded,
  satisfactionLoadChatAllLevelsSettingsFailed,

  satisfactionLoadChatGlobalSettingsRequest,
  satisfactionLoadChatGlobalSettingsSucceeded,
  satisfactionLoadChatGlobalSettingsFailed,

  satisfactionLoadChatContactLevelSettingsSucceeded,
  satisfactionLoadChatContactLevelSettingsFailed,

  satisfactionLoadChatLanguageLevelSettingsRequest,
  satisfactionLoadChatLanguageLevelSettingsSucceeded,
  satisfactionLoadChatLanguageLevelSettingsFailed,

  satisfactionSaveChatGlobalSettingsRequest,
  satisfactionSaveChatGlobalSettingsSucceeded,
  satisfactionSaveChatGlobalSettingsFailed,

  satisfactionSaveChatContactLevelSettingsRequest,
  satisfactionSaveChatContactLevelSettingsSucceeded,
  satisfactionSaveChatContactLevelSettingsFailed,

  satisfactionSaveChatLanguageLevelSettingsRequest,
  satisfactionSaveChatLanguageLevelSettingsSucceeded,
  satisfactionSaveChatLanguageLevelSettingsFailed,

  // --------------------------------------------------

  satisfactionLoadDixaMessengerAllLevelsSettingsRequest,
  satisfactionLoadDixaMessengerAllLevelsSettingsSucceeded,
  satisfactionLoadDixaMessengerAllLevelsSettingsFailed,

  satisfactionLoadDixaMessengerGlobalSettingsRequest,
  satisfactionLoadDixaMessengerGlobalSettingsSucceeded,
  satisfactionLoadDixaMessengerGlobalSettingsFailed,

  satisfactionSaveDixaMessengerGlobalSettingsRequest,
  satisfactionSaveDixaMessengerGlobalSettingsSucceeded,
  satisfactionSaveDixaMessengerGlobalSettingsFailed,

  satisfactionLoadDixaMessengerContactLevelSettingsRequest,
  satisfactionLoadDixaMessengerContactLevelSettingsSucceeded,
  satisfactionLoadDixaMessengerContactLevelSettingsFailed,

  satisfactionSaveDixaMessengerContactLevelSettingsRequest,
  satisfactionSaveDixaMessengerContactLevelSettingsSucceeded,
  satisfactionSaveDixaMessengerContactLevelSettingsFailed,

  satisfactionLoadDixaMessengerLanguageLevelSettingsRequest,
  satisfactionLoadDixaMessengerLanguageLevelSettingsSucceeded,
  satisfactionLoadDixaMessengerLanguageLevelSettingsFailed,

  satisfactionSaveDixaMessengerLanguageLevelSettingsRequest,
  satisfactionSaveDixaMessengerLanguageLevelSettingsSucceeded,
  satisfactionSaveDixaMessengerLanguageLevelSettingsFailed,
} = actions;

function genApi(
  apiAction: (payload: any, meta: any) => any,
  resultSucceededAction: (result: any) => any,
  resultFailedAction: (result: any) => any,
) {
  return function* (action) {
    try {
      const result = yield call(apiAction, action.payload, action.meta);
      yield put(resultSucceededAction(result));
    } catch (exception) {
      console.warn(exception);
      yield put(resultFailedAction(exception));
    }
  };
}

function errorHasSubErrors(error: object): error is { errors: unknown } {
  return 'errors' in error;
}

function serverErrorToString(error?: unknown): string {
  if (typeof error !== 'object') {
    if (typeof error === 'string') {
      return error;
    }

    return '';
  }

  if (!error || !('errors' in error)) return 'An unknown error occurred';

  if (errorHasSubErrors(error)) {
    if (typeof error.errors === 'string') {
      return error.errors;
    }

    if (Array.isArray(error.errors)) {
      return error.errors.join('\n');
    }

    if (
      typeof error.errors === 'object' &&
      error.errors !== null /* typeof null === 'object' */
    ) {
      return Object.entries(error.errors)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n');
    }
  }

  return error.toString();
}

function createFormSubmissionSaga<T = any>(
  formName: string,
  actions: {
    callApi: (payload: any, meta: any) => Promise<T>;
    onSuccessAction: (apiResponse: T) => Action<any>;
    onFailureAction: (apiResponse: any) => Action<any>;
  },
) {
  return function* formSubmissionSaga({
    payload,
    meta: { resolve, reject, ...otherMetaData } = {},
  }: ActionMeta<any, any>) {
    try {
      yield put(startSubmit(formName));
      const response = yield call(actions.callApi, payload, otherMetaData);
      yield put(actions.onSuccessAction(response));
      yield put(setSubmitSucceeded(formName));
      if (resolve) {
        resolve(response);
      }
    } catch (error: any) {
      yield put(actions.onFailureAction(error));
      yield put(setSubmitFailed(formName));
      yield put(
        stopSubmit(formName, {
          _error: serverErrorToString(error),
        }),
      );
      if (reject) {
        reject(error);
      }
    } finally {
      yield put(stopSubmit(formName));
    }
  };
}

function* initializeCsatSaga(action) {
  try {
    const result = yield call(api.csatApi.initializeCsat, action.payload);

    // update state after initialization.
    yield put(satisfactionInitializeSucceeded(result));

    if (action.payload === 'email') {
      yield put(actions.satisfactionLoadEmailAllLevelsSettingsRequest());
    } else if (action.payload === 'widgetchat') {
      yield put(actions.satisfactionLoadChatAllLevelsSettingsRequest());
    } else if (action.payload === 'messenger') {
      yield put(
        actions.satisfactionLoadDixaMessengerAllLevelsSettingsRequest(),
      );
    }

    yield put(
      notificationActions.notificationAdd({
        title: `Csat for ${action.payload} successfully activated`,
        text: `Now you can customize csat`,
        className: 'success',
        icon: 'i-done',
        time: 10000,
      }),
    );
  } catch (error) {
    yield satisfactionInitializeFailed(error);
    yield put(
      notificationActions.notificationAdd({
        title: 'Operation failed',
        text: `Could not activate csat for ${action.payload}.`,
        icon: 'i-info',
        className: 'danger',
        time: 10000,
      }),
    );
  }
}

function createLoadSettingsAllLevelsSaga(
  channel: Satisfaction.CSATChannelsLevel,
  actions: {
    allLevelsSucceeded: ActionFunctionAny<Action<any>>;
    globalLevelSucceeded: ActionFunctionAny<Action<any>>;
    contactLevelSucceeded: ActionFunctionAny<Action<any>>;
    languageLevelSucceeded: ActionFunctionAny<Action<any>>;
    allLevelsFailed: ActionFunctionAny<Action<any>>;
    globalLevelFailed: ActionFunctionAny<Action<any>>;
    contactLevelFailed: ActionFunctionAny<Action<any>>;
    languageLevelFailed: ActionFunctionAny<Action<any>>;
  },
) {
  return function* loadEmailSettingsAllLevelsSaga() {
    type ApiFn = typeof api.csatApi.loadSettingForAllLevels;
    type Response = PromisedValue<ReturnType<ApiFn>>;
    try {
      const response: Response = yield call(
        api.csatApi.loadSettingForAllLevels,
        channel,
      );

      yield put(actions.allLevelsSucceeded(response));

      // NOTE the order of the following calls is very important. because the reducers
      // for each action is dependant on the previous value as fallback.
      yield put(actions.globalLevelSucceeded(response.globalSettings));
      yield put(actions.contactLevelSucceeded(response.contactLevelSettings));
      yield put(actions.languageLevelSucceeded(response.languageLevelSettings));
    } catch (error) {
      yield put(actions.allLevelsFailed(error));
      yield put(actions.globalLevelFailed(error));
      yield put(actions.contactLevelFailed(error));
      yield put(actions.languageLevelFailed(error));
    }
  };
}

function createSaveContactLevelSettingsSaga(
  channel: Satisfaction.CSATChannelsLevel,
  actions: {
    succeeded: ActionFunctionAny<Action<any>>;
    failed: ActionFunctionAny<Action<any>>;
  },
) {
  return function* saveContactLevelSettingsSaga(action: any) {
    try {
      const result = yield call(
        api.csatApi.saveContactLevelSettings,
        action.payload,
        action.meta,
        channel,
      );
      yield put(actions.succeeded(result));
    } catch (error) {
      yield put(actions.failed(error));
    }
  };
}

export const satisfaction = [
  takeLatest(satisfactionInitializeRequest.toString(), initializeCsatSaga),

  takeLatest(
    satisfactionLoadEmailAllLevelsSettingsRequest.toString(),
    createLoadSettingsAllLevelsSaga('email', {
      allLevelsSucceeded: satisfactionLoadEmailAllLevelsSettingsSucceeded,
      globalLevelSucceeded: satisfactionLoadEmailGlobalSettingsSucceeded,
      contactLevelSucceeded: satisfactionLoadEmailContactLevelSettingsSucceeded,
      languageLevelSucceeded:
        satisfactionLoadEmailLanguageLevelSettingsSucceeded,
      allLevelsFailed: satisfactionLoadEmailAllLevelsSettingsFailed,
      globalLevelFailed: satisfactionLoadEmailGlobalSettingsFailed,
      contactLevelFailed: satisfactionLoadEmailContactLevelSettingsFailed,
      languageLevelFailed: satisfactionLoadEmailLanguageLevelSettingsFailed,
    }),
  ),

  takeLatest(
    satisfactionLoadChatAllLevelsSettingsRequest.toString(),
    createLoadSettingsAllLevelsSaga('widgetchat', {
      allLevelsSucceeded: satisfactionLoadChatAllLevelsSettingsSucceeded,
      globalLevelSucceeded: satisfactionLoadChatGlobalSettingsSucceeded,
      contactLevelSucceeded: satisfactionLoadChatContactLevelSettingsSucceeded,
      languageLevelSucceeded:
        satisfactionLoadChatLanguageLevelSettingsSucceeded,
      allLevelsFailed: satisfactionLoadChatAllLevelsSettingsFailed,
      globalLevelFailed: satisfactionLoadChatGlobalSettingsFailed,
      contactLevelFailed: satisfactionLoadChatContactLevelSettingsFailed,
      languageLevelFailed: satisfactionLoadChatLanguageLevelSettingsFailed,
    }),
  ),

  takeLatest(
    satisfactionLoadDixaMessengerAllLevelsSettingsRequest.toString(),
    createLoadSettingsAllLevelsSaga('messenger', {
      allLevelsSucceeded:
        satisfactionLoadDixaMessengerAllLevelsSettingsSucceeded,
      globalLevelSucceeded:
        satisfactionLoadDixaMessengerGlobalSettingsSucceeded,
      contactLevelSucceeded:
        satisfactionLoadDixaMessengerContactLevelSettingsSucceeded,
      languageLevelSucceeded:
        satisfactionLoadDixaMessengerLanguageLevelSettingsSucceeded,
      allLevelsFailed: satisfactionLoadDixaMessengerAllLevelsSettingsFailed,
      globalLevelFailed: satisfactionLoadDixaMessengerGlobalSettingsFailed,
      contactLevelFailed:
        satisfactionLoadDixaMessengerContactLevelSettingsFailed,
      languageLevelFailed:
        satisfactionLoadDixaMessengerLanguageLevelSettingsFailed,
    }),
  ),

  // -----------------------------------------------------------------------------------

  takeLatest(
    satisfactionLoadEmailGlobalSettingsRequest.toString(),
    genApi(
      api.csatApi.loadGlobalSettings,
      satisfactionLoadEmailGlobalSettingsSucceeded,
      satisfactionLoadEmailGlobalSettingsFailed,
    ),
  ),

  takeLatest(
    satisfactionLoadChatGlobalSettingsRequest.toString(),
    genApi(
      api.csatApi.loadGlobalSettings,
      satisfactionLoadChatGlobalSettingsSucceeded,
      satisfactionLoadChatGlobalSettingsFailed,
    ),
  ),

  takeLatest(
    satisfactionLoadDixaMessengerGlobalSettingsRequest.toString(),
    genApi(
      api.csatApi.loadGlobalSettings,
      satisfactionLoadDixaMessengerGlobalSettingsSucceeded,
      satisfactionLoadDixaMessengerGlobalSettingsFailed,
    ),
  ),

  // -----------------------------------------------------------------------------------

  takeLatest(
    satisfactionSaveEmailGlobalSettingsRequest.toString(),
    createFormSubmissionSaga(EmailPanelForms.GlobalSettingsForm, {
      callApi: api.csatApi.saveGlobalSettings,
      onSuccessAction: satisfactionSaveEmailGlobalSettingsSucceeded,
      onFailureAction: satisfactionSaveEmailGlobalSettingsFailed,
    }),
  ),

  takeLatest(
    satisfactionSaveChatGlobalSettingsRequest.toString(),
    createFormSubmissionSaga(ChatPanelForms.GlobalSettingsForm, {
      callApi: api.csatApi.saveGlobalSettings,
      onSuccessAction: satisfactionSaveChatGlobalSettingsSucceeded,
      onFailureAction: satisfactionSaveChatGlobalSettingsFailed,
    }),
  ),

  takeLatest(
    satisfactionSaveDixaMessengerGlobalSettingsRequest.toString(),
    createFormSubmissionSaga(DixaMessengerPanelForms.GlobalSettingsForm, {
      callApi: api.csatApi.saveGlobalSettings,
      onSuccessAction: satisfactionSaveDixaMessengerGlobalSettingsSucceeded,
      onFailureAction: satisfactionSaveDixaMessengerGlobalSettingsFailed,
    }),
  ),

  // -----------------------------------------------------------------------------------

  takeLatest(
    satisfactionLoadEmailContactLevelSettingsRequest.toString(),
    genApi(
      api.csatApi.loadContactLevelSettings,
      satisfactionLoadEmailContactLevelSettingsSucceeded,
      satisfactionLoadEmailContactLevelSettingsFailed,
    ),
  ),

  takeLatest(
    satisfactionLoadDixaMessengerContactLevelSettingsRequest.toString(),
    genApi(
      api.csatApi.loadContactLevelSettings,
      satisfactionLoadDixaMessengerContactLevelSettingsSucceeded,
      satisfactionLoadDixaMessengerContactLevelSettingsFailed,
    ),
  ),

  // -----------------------------------------------------------------------------------

  takeLatest(
    satisfactionSaveEmailContactLevelSettingsRequest.toString(),
    createSaveContactLevelSettingsSaga('email', {
      succeeded: satisfactionSaveEmailContactLevelSettingsSucceeded,
      failed: satisfactionSaveEmailContactLevelSettingsFailed,
    }),
  ),

  takeLatest(
    satisfactionSaveChatContactLevelSettingsRequest.toString(),
    createSaveContactLevelSettingsSaga('widgetchat', {
      succeeded: satisfactionSaveChatContactLevelSettingsSucceeded,
      failed: satisfactionSaveChatContactLevelSettingsFailed,
    }),
  ),

  takeLatest(
    satisfactionSaveDixaMessengerContactLevelSettingsRequest.toString(),
    createSaveContactLevelSettingsSaga('messenger', {
      succeeded: satisfactionSaveDixaMessengerContactLevelSettingsSucceeded,
      failed: satisfactionSaveDixaMessengerContactLevelSettingsFailed,
    }),
  ),

  // -----------------------------------------------------------------------------------

  takeLatest(
    satisfactionLoadEmailLanguageLevelSettingsRequest.toString(),
    genApi(
      api.csatApi.loadLanguageLevelSettings,
      satisfactionLoadEmailLanguageLevelSettingsSucceeded,
      satisfactionLoadEmailLanguageLevelSettingsFailed,
    ),
  ),

  takeLatest(
    satisfactionLoadChatLanguageLevelSettingsRequest.toString(),
    genApi(
      api.csatApi.loadLanguageLevelSettings,
      satisfactionLoadChatLanguageLevelSettingsSucceeded,
      satisfactionLoadChatLanguageLevelSettingsFailed,
    ),
  ),

  takeLatest(
    satisfactionLoadDixaMessengerLanguageLevelSettingsRequest.toString(),
    genApi(
      api.csatApi.loadLanguageLevelSettings,
      satisfactionLoadDixaMessengerLanguageLevelSettingsSucceeded,
      satisfactionLoadDixaMessengerLanguageLevelSettingsFailed,
    ),
  ),

  // -----------------------------------------------------------------------------------

  takeLatest(
    satisfactionSaveEmailLanguageLevelSettingsRequest.toString(),
    createFormSubmissionSaga(EmailPanelForms.LanguageCustomizationPageForm, {
      callApi: api.csatApi.saveLanguageLevelSettings,
      onSuccessAction: satisfactionSaveEmailLanguageLevelSettingsSucceeded,
      onFailureAction: satisfactionSaveEmailLanguageLevelSettingsFailed,
    }),
  ),

  takeLatest(
    satisfactionSaveChatLanguageLevelSettingsRequest.toString(),
    createFormSubmissionSaga(ChatPanelForms.LanguageCustomizationPageForm, {
      callApi: api.csatApi.saveLanguageLevelSettings,
      onSuccessAction: satisfactionSaveChatLanguageLevelSettingsSucceeded,
      onFailureAction: satisfactionSaveChatLanguageLevelSettingsFailed,
    }),
  ),

  takeLatest(
    satisfactionSaveDixaMessengerLanguageLevelSettingsRequest.toString(),
    createFormSubmissionSaga(
      DixaMessengerPanelForms.LanguageCustomizationPageForm,
      {
        callApi: api.csatApi.saveLanguageLevelSettings,
        onSuccessAction:
          satisfactionSaveDixaMessengerLanguageLevelSettingsSucceeded,
        onFailureAction:
          satisfactionSaveDixaMessengerLanguageLevelSettingsFailed,
      },
    ),
  ),

  takeLatest(
    satisfactionSaveChatLanguageLevelSettingsSucceeded.toString(),
    function* () {
      pubSub.publish('navigate', {
        path: '/settings/satisfaction/chat',
      });
    },
  ),
];
